import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const fmt  = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => Number(n || 0).toLocaleString('en-IN');

const currentYear = new Date().getFullYear();
const YEARS  = Array.from({ length: 5 }, (_, i) => currentYear - i);
const MONTHS = [
  { value: '', label: 'All Months (Annual)' },
  { value: 1, label: 'April' }, { value: 2, label: 'May' }, { value: 3, label: 'June' },
  { value: 4, label: 'July' }, { value: 5, label: 'August' }, { value: 6, label: 'September' },
  { value: 7, label: 'October' }, { value: 8, label: 'November' }, { value: 9, label: 'December' },
  { value: 10, label: 'January' }, { value: 11, label: 'February' }, { value: 12, label: 'March' },
];

const TAX_LABELS = { igst: 'IGST (Inter-state)', cgst_sgst: 'CGST + SGST (Intra-state)', export: 'Export / LUT (0%)', none: 'Exempt / No Tax' };

function KPICard({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', borderLeft: `4px solid ${color}`, minWidth: 160 }}>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: 'monospace' }}>₹{fmt(value)}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function GSTReport() {
  const [year,  setYear]  = useState(currentYear);
  const [month, setMonth] = useState('');
  const [data,  setData]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [tab, setTab] = useState('summary'); // summary | invoices | hsn | monthly

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { year, ...(month ? { month } : {}) };
      const { data: res } = await api.get('/accounting/gst-report', { params });
      setData(res.data);
    } catch { setError('Failed to load GST report.'); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['Order #','Date','Party','GSTIN','State Code','Tax Type','Tax Rate','Taxable Value','IGST','CGST','SGST','Freight Tax','Total'],
      ...data.invoices.map(r => {
        const igst  = r.tax_type === 'igst'      ? r.tax_amount + r.freight_tax : 0;
        const cgst  = r.tax_type === 'cgst_sgst' ? (r.tax_amount + r.freight_tax) / 2 : 0;
        const sgst  = r.tax_type === 'cgst_sgst' ? (r.tax_amount + r.freight_tax) / 2 : 0;
        return [r.order_number, r.created_at?.slice(0,10), r.party_name, r.client_gstin||'', r.client_state_code||'', r.tax_type, r.tax_rate+'%', r.taxable_value.toFixed(2), igst.toFixed(2), cgst.toFixed(2), sgst.toFixed(2), r.freight_tax.toFixed(2), r.total];
      })
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url;
    a.download = `GST_Report_${year}${month ? '_M'+month : ''}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div style={{ paddingTop: 24, paddingBottom: 40, padding: '0 24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>GST Report</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>
            Outward supply summary as per GST rules — GSTR-1 / GSTR-3B reference
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 600 }}>
            {YEARS.map(y => <option key={y} value={y}>FY {y}-{String(y+1).slice(-2)}</option>)}
          </select>
          <select value={month} onChange={e => setMonth(e.target.value)}
            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}>
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <button className="btn btn-primary" onClick={load}>Apply</button>
          <button className="btn btn-secondary" onClick={exportCSV}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 80 }}><div className="spinner spinner-lg" /></div>}
      {error   && <div className="alert alert-error">{error}</div>}

      {data && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
            <KPICard label="Total Taxable Value" value={data.totals.taxable}       color="#374151" />
            <KPICard label="IGST Collected"      value={data.totals.igst}          color="#2563eb" sub="Inter-state supplies" />
            <KPICard label="CGST Collected"      value={data.totals.cgst}          color="#7c3aed" sub="Intra-state (CGST)" />
            <KPICard label="SGST Collected"      value={data.totals.sgst}          color="#7c3aed" sub="Intra-state (SGST)" />
            <KPICard label="Total GST Payable"   value={data.totals.total_gst}     color="#dc2626" />
            <KPICard label="Export Value (0%)"   value={data.totals.export_value}  color="#059669" sub="LUT/Zero-rated" />
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 24 }}>
            {[['summary','Tax Summary'], ['invoices','Invoice-wise'], ['hsn','HSN Summary'], ['monthly','Monthly Breakup']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                style={{ padding: '9px 18px', border: 'none', borderBottom: tab === k ? '2px solid #2563eb' : '2px solid transparent', background: 'none', fontWeight: tab === k ? 700 : 400, color: tab === k ? '#2563eb' : '#6b7280', cursor: 'pointer', marginBottom: -2, fontSize: 14 }}>
                {l}
              </button>
            ))}
          </div>

          {/* Tax Summary tab — GSTR-3B style */}
          {tab === 'summary' && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 15 }}>
                Table 3.1 — Details of Outward Supplies (GSTR-3B Reference)
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1e40af', color: '#fff' }}>
                    {['Nature of Supply','Taxable Value (₹)','IGST (₹)','CGST (₹)','SGST (₹)','Cess (₹)','Invoices'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Invoices' ? 'center' : 'right', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'igst',      label: '(a) Taxable inter-state outward supplies' },
                    { key: 'cgst_sgst', label: '(b) Taxable intra-state outward supplies' },
                    { key: 'export',    label: '(c) Zero-rated supplies (Exports / LUT)' },
                    { key: 'none',      label: '(d) Exempt / Nil-rated / Non-GST supplies' },
                  ].map(row => {
                    const rows = data.summary.filter(s => s.tax_type === row.key);
                    const taxable = rows.reduce((s, r) => s + Number(r.taxable_value), 0);
                    const tax     = rows.reduce((s, r) => s + Number(r.tax_amount) + Number(r.freight_tax), 0);
                    const count   = rows.reduce((s, r) => s + Number(r.invoice_count), 0);
                    const igst    = row.key === 'igst'      ? tax : 0;
                    const cgst    = row.key === 'cgst_sgst' ? tax / 2 : 0;
                    const sgst    = row.key === 'cgst_sgst' ? tax / 2 : 0;
                    return (
                      <tr key={row.key} style={{ borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <td style={{ padding: '10px 14px', fontWeight: 500, color: '#374151' }}>{row.label}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(taxable)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#2563eb' }}>{fmt(igst)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#7c3aed' }}>{fmt(cgst)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#7c3aed' }}>{fmt(sgst)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#9ca3af' }}>0.00</td>
                        <td style={{ padding: '10px 14px', textAlign: 'center', color: '#6b7280' }}>{count}</td>
                      </tr>
                    );
                  })}
                  {/* Total row */}
                  <tr style={{ background: '#1e40af', color: '#fff', fontWeight: 700 }}>
                    <td style={{ padding: '12px 14px', fontSize: 13 }}>TOTAL</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.taxable)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.igst)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.cgst)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.sgst)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'monospace' }}>0.00</td>
                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                      {data.summary.reduce((s, r) => s + Number(r.invoice_count), 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ padding: '10px 20px', background: '#fffbeb', borderTop: '1px solid #fde68a', fontSize: 12, color: '#92400e' }}>
                <b>Note:</b> This report is for reference only. Always verify with your CA / GST portal (GSTIN) before filing. Freight GST is included in the tax amounts.
              </div>
            </div>
          )}

          {/* Invoice-wise detail tab */}
          {tab === 'invoices' && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontSize: 13, color: '#6b7280' }}>
                Showing {data.invoices.length} invoice(s)
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#374151', color: '#fff' }}>
                      {['Invoice #','Date','Party','GSTIN','State','Type','Rate','Taxable (₹)','IGST (₹)','CGST (₹)','SGST (₹)','Freight Tax','Total (₹)'].map(h => (
                        <th key={h} style={{ padding: '9px 10px', textAlign: h.includes('₹') ? 'right' : 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.invoices.map((r, i) => {
                      const igst = r.tax_type === 'igst'      ? r.tax_amount + r.freight_tax : 0;
                      const cgst = r.tax_type === 'cgst_sgst' ? (r.tax_amount + r.freight_tax) / 2 : 0;
                      const sgst = r.tax_type === 'cgst_sgst' ? (r.tax_amount + r.freight_tax) / 2 : 0;
                      return (
                        <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>{r.order_number}</td>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: '#374151' }}>{r.created_at?.slice(0,10)}</td>
                          <td style={{ padding: '8px 10px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.party_name}</td>
                          <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, color: '#059669' }}>{r.client_gstin || '—'}</td>
                          <td style={{ padding: '8px 10px', color: '#6b7280' }}>{r.client_state_code || '—'}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <span style={{ padding: '2px 6px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                              background: { igst:'#dbeafe', cgst_sgst:'#ede9fe', export:'#d1fae5', none:'#f3f4f6' }[r.tax_type],
                              color:      { igst:'#1d4ed8', cgst_sgst:'#5b21b6', export:'#059669', none:'#374151' }[r.tax_type] }}>
                              {r.tax_type === 'cgst_sgst' ? 'CGST+SGST' : r.tax_type?.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{r.tax_rate}%</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(r.taxable_value)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#2563eb' }}>{fmt(igst)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#7c3aed' }}>{fmt(cgst)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#7c3aed' }}>{fmt(sgst)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#6b7280' }}>{fmt(r.freight_tax)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmt(r.total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* HSN Summary tab */}
          {tab === 'hsn' && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 15 }}>
                HSN-wise Summary (Annexure to GSTR-1)
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#374151', color: '#fff' }}>
                    {['HSN Code','Product Name','Total Qty','Taxable Value (₹)','Tax Amount (₹)'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: h.includes('₹') || h === 'Total Qty' ? 'right' : 'left', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.hsn.map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '9px 14px', fontWeight: 700, fontFamily: 'monospace', color: '#059669' }}>{r.hsn_code}</td>
                      <td style={{ padding: '9px 14px', color: '#374151' }}>{r.product_name}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtN(r.total_qty)}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(r.taxable_value)}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#dc2626', fontWeight: 600 }}>{fmt(r.tax_amount)}</td>
                    </tr>
                  ))}
                  {data.hsn.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No HSN data found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Monthly Breakup tab */}
          {tab === 'monthly' && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 15 }}>
                Month-wise GST Summary — FY {year}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1e40af', color: '#fff' }}>
                    {['Month','Invoices','Taxable Value (₹)','IGST (₹)','CGST (₹)','SGST (₹)','Total GST (₹)','Export (₹)'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Month' ? 'left' : 'right', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.monthly.map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '9px 14px', fontWeight: 600, color: '#374151' }}>{r.month}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', color: '#6b7280' }}>{r.invoice_count}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(r.total_taxable)}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#2563eb' }}>{fmt(r.igst)}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#7c3aed' }}>{fmt(r.cgst)}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#7c3aed' }}>{fmt(r.sgst)}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#dc2626' }}>{fmt(r.igst + r.cgst + r.sgst)}</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#059669' }}>{fmt(r.export_value)}</td>
                    </tr>
                  ))}
                  {/* Totals */}
                  <tr style={{ background: '#1e40af', color: '#fff', fontWeight: 700 }}>
                    <td style={{ padding: '11px 14px' }}>TOTAL</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right' }}>{data.summary.reduce((s,r) => s + Number(r.invoice_count), 0)}</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.taxable)}</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.igst)}</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.cgst)}</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.sgst)}</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.total_gst)}</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.totals.export_value)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
